import type { ReactiveString, RequestOptions } from "../types/request";
import type { ServiceName } from "../types/service"

/**
 * @typedef {Object} loginAuthFormData
 * @prop {string|null=} recaptchaToken - A token that lets the site know that the request does not come from a bot.
 * @prop {string|null=} recaptchaPartner - A token that lets the site know that the request does not come from a bot. (google, tencent)
 * @prop {string|null=} recaptchaRandomStr - For Tencent Captcha, Rnadom String generated by the Captcha.
 * @prop {string|null=} email - The user's email address. Required for login with a recaptcha token.
 * @prop {string|null=} password - The user's password. Required for login with a recaptcha token.
 * @prop {string|null=} sso_partner - SSO Partner for login. Required to login via SSO.
 * @prop {string|null=} sso_credential - Credentials for SSO login Required to login via SSO.
 * @prop {string=} platform_id - Tengiva Platform ID to sign in to
 */
export type loginAuthFormData = {
	recaptchaToken?: string|null;
	recaptchaPartner?: string|null;
	recaptchaRandomStr?: string|null;
	email?: string|null;
	password?: string|null;
	sso_partner?: string|null;
	sso_credential?: string|null;
	platform_id?: string;
};

/**
 * @typedef {Object} createSsoAuthorizationAuthFormData
 * @prop {string|null=} recaptchaToken - A token that lets the site know that the request does not come from a bot.
 * @prop {string|null=} recaptchaPartner - A token that lets the site know that the request does not come from a bot. (google, tencent)
 * @prop {string|null=} recaptchaRandomStr - For Tencent Captcha, Rnadom String generated by the Captcha.
 * @prop {string|null=} email - The user's email address. Required for login with a recaptcha token.
 * @prop {string|null=} password - The user's password. Required for login with a recaptcha token.
 * @prop {string|null=} sso_partner - SSO Partner for login. Required to login via SSO.
 * @prop {string|null=} sso_credential - Credentials for SSO login Required to login via SSO.
 * @prop {string} platform - Tengiva Platform, as it is in the Tengiva-Platform header
 */
export type createSsoAuthorizationAuthFormData = {
	recaptchaToken?: string|null;
	recaptchaPartner?: string|null;
	recaptchaRandomStr?: string|null;
	email?: string|null;
	password?: string|null;
	sso_partner?: string|null;
	sso_credential?: string|null;
	platform: string;
};

/**
 * @typedef {Object} createCheckoutAuthorizationAuthFormData
 * @prop {string} platform_id - Tengiva Platform ID to checkout from
 * @prop {string} cart_id - Tengiva Cart ID to checkout with
 */
export type createCheckoutAuthorizationAuthFormData = {
	platform_id: string;
	cart_id: string;
};

/**
 * @typedef {Object} activateAuthFormData Data to update about the user
 * @prop {string=} email - User's Email Address
 * @prop {string=} first_name - User's First Name
 * @prop {string=} last_name - User's Last Name
 * @prop {Number=} phone - User's phone number, up to 15 digits
 * @prop {Number=} phone_ext - User's phone number extension, up to 6 digits
 * @prop {string=} password - User's password, required if not using SSO
 * @prop {string=} confirm_password - Confirm User's password, required if not using SSO
 * @prop {string=} sso_partner - SSO Partner for login, required to sign up via SSO.
 * @prop {string=} sso_credential - Credentials for SSO login, required to sign up via SSO.
 * @prop {{}=} platform
 * @prop {boolean=} initial - Is it the initial member, should be in the activation link.
 */
export type activateAuthFormData = {
	email?: string;
	first_name?: string;
	last_name?: string;
	phone?: Number;
	phone_ext?: Number;
	password?: string;
	confirm_password?: string;
	sso_partner?: string;
	sso_credential?: string;
	platform?: {};
	initial?: boolean;
};

/**
 * @typedef {Object} addIndividualMemberFormData Individual Member & User data
 * @prop {{}} user - User data
 * @prop {string=} email_template - Email template to send to the user to activate their account (defaults to the Tengiva email) (Covolt, Marketplace, TNGV)
 * @prop {{}=} platform
 */
export type addIndividualMemberFormData = {
	user: {};
	email_template?: string;
	platform?: {};
};

/**
 * @typedef {Object} editIndividualMemberFormData Individual Member's data
 * @prop {string=} user_id - User Id
 * @prop {string=} address_id - Address Id
 * @prop {string=} cart_id - Cart Id
 */
export type editIndividualMemberFormData = {
	user_id?: string;
	address_id?: string;
	cart_id?: string;
};

/**
 * @typedef {Object} addOrganizationMemberFormData
 * @prop {{}} organization - Organization Data
 * @prop {{}} user - User's Information
 */
export type addOrganizationMemberFormData = {
	organization: {};
	user: {};
};

/**
 * @typedef {Object} inviteOrganizationMemberFormData
 * @prop {string|null=} service_id - Service Id
 * @prop {{}=} organization - Organization Data
 * @prop {{}} user - User's Information
 * @prop {{}=} roles - List of Roles to assign to the User
 * @prop {boolean=} super_admin - Should this User be a Super Admin?
 */
export type inviteOrganizationMemberFormData = {
	service_id?: string|null;
	organization?: {};
	user: {};
	roles?: {};
	super_admin?: boolean;
};

/**
 * @typedef {Object} changePasswordFormData
 * @prop {string} current_pw - the user's current password.
 * @prop {string} new_pw - the user's new password.
 * @prop {string} confirm_pw - the user's new password confirmation.
 */
export type changePasswordFormData = {
	current_pw: string;
	new_pw: string;
	confirm_pw: string;
};

/**
 * @typedef {Object} recoverPasswordFormData
 * @prop {string} email - The user's email.
 * @prop {string|null=} platform_id - ID of the platform the request is originating from.
 */
export type recoverPasswordFormData = {
	email: string;
	platform_id?: string|null;
};

/**
 * @typedef {Object} changeUsingRecoveryCodePasswordFormData
 * @prop {string} id - the user's id.
 * @prop {string} recovery_code - The password recovery code.
 * @prop {string} password - The user's new password
 * @prop {string} confirm_password - The password confirmation.
 */
export type changeUsingRecoveryCodePasswordFormData = {
	id: string;
	recovery_code: string;
	password: string;
	confirm_password: string;
};

/**
 * @typedef {Object} sendNewTemporaryPasswordPasswordFormData
 * @prop {string} id - The user's id.
 */
export type sendNewTemporaryPasswordPasswordFormData = {
	id: string;
};

/**
 * @typedef {Object} sendNewTemporaryPasswordAndWelcomeEmailPasswordFormData
 * @prop {string} id - The user's id.
 */
export type sendNewTemporaryPasswordAndWelcomeEmailPasswordFormData = {
	id: string;
};

/**
 * @typedef {Object} hasPermissionPermissionFormData
 * @prop {string} user_id - User ID to check
 * @prop {string|null=} organization_id - Organization's ID (defaults to the User's organization)
 * @prop {string} category - Category to check permissions for
 * @prop {Number=} level - Minimum level required for access (will return the level if no level is provided)
 */
export type hasPermissionPermissionFormData = {
	user_id: string;
	organization_id?: string|null;
	category: string;
	level?: Number;
};

/**
 * @typedef {Object} hasPermissionsPermissionFormData
 * @prop {string} user_id - User ID to check
 * @prop {string|null=} organization_id - Organization's ID (defaults to the User's organization)
 * @prop {{}} permissions
 */
export type hasPermissionsPermissionFormData = {
	user_id: string;
	organization_id?: string|null;
	permissions: {};
};

/**
 * @typedef {Object} addRoleFormData Role's data
 * @prop {string=} user_type_name - User Type Name
 * @prop {string} category - Category of pages/endpoints for this role
 * @prop {boolean=} tengiva - Is this role only for Tengiva Users?
 * @prop {string=} name - Name to display (will default to the category if not set)
 * @prop {string=} description - Description
 * @prop {Number=} status - Status
 */
export type addRoleFormData = {
	user_type_name?: string;
	category: string;
	tengiva?: boolean;
	name?: string;
	description?: string;
	status?: Number;
};

/**
 * @typedef {Object} editRoleFormData Role's data
 * @prop {string=} user_type_name - User Type Name
 * @prop {string=} category - Category of pages/endpoints for this role
 * @prop {boolean=} tengiva - Is this role only for Tengiva Users?
 * @prop {string=} name - Name to display (will default to the category if not set)
 * @prop {string=} description - Description
 * @prop {Number=} status - Status
 */
export type editRoleFormData = {
	user_type_name?: string;
	category?: string;
	tengiva?: boolean;
	name?: string;
	description?: string;
	status?: Number;
};

/**
 * @typedef {Object} addSsoLoginFormData SSO Login's data
 * @prop {string} partner - Partner Name
 * @prop {string} credential - Credentials for SSO login
 */
export type addSsoLoginFormData = {
	partner: string;
	credential: string;
};

/**
 * @typedef {Object} listUserInvitesFormData Pagination parameters (can be provided via query parameters for GET requests).
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listUserInvitesFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} listUsersActivitiesFormData
 * @prop {{}=} filters
 * @prop {string|null=} group_by - Name of the field to group results by (user_id, organization_id)
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listUsersActivitiesFormData = {
	filters?: {};
	group_by?: string|null;
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addUsersActivityFormData Users Activity's data
 * @prop {string} user_id - User Id
 * @prop {string} platform_variant_name - Platform Variant Name
 * @prop {string|null=} platform_id - Platform Id
 * @prop {string} action - Action (activate, checkout, recover_password, refresh_token, refresh_user, sign_in, sign_up)
 */
export type addUsersActivityFormData = {
	user_id: string;
	platform_variant_name: string;
	platform_id?: string|null;
	action: string;
};

/**
 * @typedef {Object} editUserFormData User's data
 * @prop {string} id - The user's id.
 * @prop {string=} email - Email
 * @prop {string=} first_name - First Name
 * @prop {string=} last_name - Last Name
 * @prop {string=} language - Language
 * @prop {string=} currency - Currency
 * @prop {boolean=} needs_password_change - Needs Password Change
 * @prop {Number=} status - Status
 * @prop {string=} phone - Phone
 * @prop {string=} phone_ext - Phone Ext
 * @prop {string=} picture_id - Picture Id
 * @prop {string|null=} country_alpha2 - Two-letter country code
 */
export type editUserFormData = {
	id: string;
	email?: string;
	first_name?: string;
	last_name?: string;
	language?: string;
	currency?: string;
	needs_password_change?: boolean;
	status?: Number;
	phone?: string;
	phone_ext?: string;
	picture_id?: string;
	country_alpha2?: string|null;
};

/**
 * @typedef {Object} emailExistsUserFormData
 * @prop {string} email - A user's email.
 */
export type emailExistsUserFormData = {
	email: string;
};

/**
 * @typedef {Object} listAllUserFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listAllUserFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} inviteUserFormData
 * @prop {{}=} organization - Organization Data
 * @prop {{}} user - User's Information
 * @prop {{}=} roles - List of Roles to assign to the User
 * @prop {boolean=} super_admin - Should this User be a Super Admin?
 */
export type inviteUserFormData = {
	organization?: {};
	user: {};
	roles?: {};
	super_admin?: boolean;
};

/**
 * @typedef {Object} sendActivationEmailUserFormData
 * @prop {{}|null=} platform
 */
export type sendActivationEmailUserFormData = {
	platform?: {}|null;
};

/**
 * @typedef {Object} getUsersByOrganizationIdUserFormData Pagination parameters (can be provided via query parameters for GET requests).
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type getUsersByOrganizationIdUserFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} giveRoleUserFormData
 * @prop {string} role - Role ID or slug
 * @prop {string|null=} organization - Organization ID (will default to the User's default organization if not set)
 * @prop {Number=} level - Access level for this role where 1 is least & 99 is most (defaults to 50 for new roles)
 * @prop {string|null=} expiration - Date & time this role will expire, in the format YYYY-MM-DD HH:mm:ss (UTC timezone)
 */
export type giveRoleUserFormData = {
	role: string;
	organization?: string|null;
	level?: Number;
	expiration?: string|null;
};

/**
 * @typedef {Object} revokeRoleUserFormData
 * @prop {string} role - Role ID or slug
 * @prop {string|null=} organization - Organization ID (will default to the User's default organization if not set)
 */
export type revokeRoleUserFormData = {
	role: string;
	organization?: string|null;
};

/**
 * @typedef {Object} listUsersDataFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listUsersDataFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewByFieldAndPlatformAndUserIdUsersDataFormData Users Data's data
 * @prop {string|null=} user_id - User Id
 * @prop {string} field - Users Data Field
 * @prop {string|null=} platform - Platform Name (like in the Tengiva-Platform Header)
 */
export type viewByFieldAndPlatformAndUserIdUsersDataFormData = {
	user_id?: string|null;
	field: string;
	platform?: string|null;
};

/**
 * @typedef {Object} addUsersDataFormData Users Data's data
 * @prop {string=} user_id - User Id
 * @prop {string|null=} platform - Platform
 * @prop {string} field - Field
 * @prop {string|null=} value - Value
 */
export type addUsersDataFormData = {
	user_id?: string;
	platform?: string|null;
	field: string;
	value?: string|null;
};

/**
 * @typedef {Object} editUsersDataFormData Users Data's data
 * @prop {string=} user_id - User Id
 * @prop {string|null=} platform - Platform
 * @prop {string=} field - Field
 * @prop {string|null=} value - Value
 */
export type editUsersDataFormData = {
	user_id?: string;
	platform?: string|null;
	field?: string;
	value?: string|null;
};

const service = {
	name: "URM" as ServiceName,
	version: 1
};

export const useURMApi = () => {
	/**
	 * Call an endpoint on the URM
	 *
	 * Sets the service to the Request and forwards it to the TengivaApi.call() function
	 * @param {ReactiveString} endpoint - Endpoint to request
	 * @param {RequestOptions} options - Additonal options for the Request
	 * @returns {Promise<any>}
	 */
	const call = async <T = any>(endpoint: ReactiveString, options: RequestOptions) => {
		options = {
			...options,
			service
		};
		return await useTengivaApi<T>(endpoint, options);
	};

	/**
	 * Login to a user's account
	 * 
	 * Can login in via Form with a Recaptcha Token, or via SSO, with the name of the SSO Partner & Credentials
	 * Once logged in, creates a Session & returns a JWT with their user data & permissions
	 * Form/Recaptcha login:
	 * A valid recaptchaToken, email & password are required to log in
	 * SSO Login
	 * A valid SSO partner & credential are required to log in.
	 * The user must have enabled log in through this partner for this to work.
	 * The partner can either be "Google" or "Tengiva" & the credentials are a JWT
	 * 
	 * @param {loginAuthFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const loginAuth = async <T = any>(formData: loginAuthFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/auth/login`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create an SSO Authorization to login to a user's account
	 * 
	 * # Request:
	 * Requests must comme from Frontend-AUTH, with the body specifying
	 * the platform where the user wants to log in.
	 * They can use credentials in via Form with a Recaptcha Token,
	 * via SSO, with the name of the SSO Partner & Credentials
	 * or with an Identity token.
	 * ### Form/Recaptcha login:
	 * A valid recaptchaToken, email & password are required to log in
	 * ### SSO Login:
	 * A valid SSO partner & credential are required to log in.
	 * The user must have enabled log in through this partner for this to work.
	 * For now, the only supported partner is "Google" & the credential is a JWT.
	 * A Tengiva SSO Authorization Token generated by this endpoint cannot be used to create a new one.
	 * ### Identity/User Token:
	 * A user who is already logged in can issue an SSO authorization for another platform
	 * No Credentials are required in the body.
	 * This doesn't issue a new Identity token.
	 * # Response
	 * ### user:
	 * The User info, like `/auth/user`
	 * ### token:
	 * A Identity token for the logged in User.
	 * For Frontend-AUTH only to identify the User. This token shouldn't be shared with any other applications.
	 * Will be null if the request was made with an existing token
	 * ### sso_token:
	 * SSO Authorization Token, to authorize the User to connect from another platform.
	 * This token this not sufficient to connect to the rest of the Tengiva API.
	 * It can only be used to generate a new, valid User/Identity token via the login endpoint.
	 * 
	 * @param {createSsoAuthorizationAuthFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const createSsoAuthorizationAuth = async <T = any>(formData: createSsoAuthorizationAuthFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/auth/create_sso_authorization`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create a Checkout Authorization to checkout with  user's account
	 * 
	 * # Request:
	 * Requests must comme from one of the e-commerce Frontends (TNGV, Martketpace), with the body specifying
	 * the platform id where the user is coming from & the cart they want to check out.
	 * Their Identity token is used as their credentials.
	 * # Response
	 * ### user:
	 * The User info, like `/auth/user`
	 * ### token:
	 * A Identity token for the logged in User.
	 * For Frontend-AUTH only to identify the User. This token shouldn't be shared with any other applications.
	 * Will be null if the request was made with an existing token
	 * ### checkout_token:
	 * Checkout Authorization Token, to authorize the User to connect at Checkout from another platform.
	 * This token this not sufficient to connect to the rest of the Tengiva API.
	 * It can only be used to generate a new, valid User/Identity token via the login endpoint, from the Checkout.
	 * 
	 * @param {createCheckoutAuthorizationAuthFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const createCheckoutAuthorizationAuth = async <T = any>(formData: createCheckoutAuthorizationAuthFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/auth/create_checkout_authorization`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Refresh the current token
	 * Gets the up-to-date user, session & guest data from the database
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const tokenAuth = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/auth/token`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get user data about the current User
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const userAuth = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/auth/user`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Activate a user by providing an activation code & provides them with a Token
	 * 
	 * Can also set the password & some basic user details
	 * 
	 * @param {ReactiveString} user_id - 
	 * @param {ReactiveString} code - 
	 * @param {activateAuthFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const activateAuth = async <T = any>(user_id: ReactiveString, code: ReactiveString, formData?: activateAuthFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/auth/activate`,
			[
				user_id, 
				code
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} guest - Guest id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteGuest = async <T = any>(guest: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/guests/delete`,
			[
				guest
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listIndividualMembers = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/individual_members/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View a specific Individual Member
	 * 
	 * Will the get the current users' Individual Member if no ID specified
	 * 
	 * @param {ReactiveString} id - Individual Members id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewIndividualMember = async <T = any>(id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/individual_members/view`,
			[
				id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addIndividualMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addIndividualMember = async <T = any>(formData: addIndividualMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/individual_members/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} individualMember - Individual Member id.
	 * @param {editIndividualMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editIndividualMember = async <T = any>(individualMember: ReactiveString, formData: editIndividualMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/individual_members/edit`,
			[
				individualMember
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} individualMember - Individual Member id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteIndividualMember = async <T = any>(individualMember: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/individual_members/delete`,
			[
				individualMember
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get IndividualMember's data by user_id
	 * 
	 * @param {ReactiveString} individualMember - Individual Member user_id
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewByUserIdIndividualMember = async <T = any>(individualMember: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/individual_members/viewByUserId`,
			[
				individualMember
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Organization Member matching it's unique key (organization_id, user_id)
	 * 
	 * If passing all the fields, the results will only include 1 record
	 * Otherwise, there may be several records in the results
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {ReactiveString} user_id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdAndUserIdOrganizationMember = async <T = any>(organization_id: ReactiveString, user_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organization_members/viewAllByOrganizationIdAndUserId`,
			[
				organization_id, 
				user_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create the Super Admin user who will set up a new Organization in Tengiva
	 * 
	 * Done after they signed a contract & the Organization was set up.
	 * Creates an Admin User associated to the Organization
	 * Once ready, use users/sendActivationEmail to send them the invite email
	 * 
	 * @param {addOrganizationMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOrganizationMember = async <T = any>(formData: addOrganizationMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/organization_members/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Change the Organization for a test account
	 * 
	 * Used so the same tests accounts can be reused to test several organizations
	 * 
	 * @param {ReactiveString} user - 
	 * @param {ReactiveString} organization_id - will remove the current organization tie null
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const changeTestAccountOrganizationOrganizationMember = async <T = any>(user: ReactiveString, organization_id: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organization_members/changeTestAccountOrganization`,
			[
				user, 
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Invite an email to create an Organization Member account
	 * 
	 * This endpoint will be triggered when an organization, with access to Frontend-SYSTEM
	 * creates a new member to their organization using the my-team page.
	 * 
	 * @param {inviteOrganizationMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const inviteOrganizationMember = async <T = any>(formData: inviteOrganizationMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/organization_members/invite`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Change the current users password
	 * 
	 * Requires the old & new password to be provided
	 * 
	 * @param {changePasswordFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const changePassword = async <T = any>(formData: changePasswordFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/password/change`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Set a user's password recovery code and sends it in an email.
	 * The recovery code can then be used to call the changepasswordusingrecoverycode endpoint.
	 * 
	 * @param {recoverPasswordFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const recoverPassword = async <T = any>(formData: recoverPasswordFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/password/recover`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Change a user's password by providing a password recovery code
	 * 
	 * Next step for a User after calling /password/recover
	 * Requires the new password to be provided as well.
	 * 
	 * @param {changeUsingRecoveryCodePasswordFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const changeUsingRecoveryCodePassword = async <T = any>(formData: changeUsingRecoveryCodePasswordFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/password/changeUsingRecoveryCode`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Changes the user's password to a random password and sends the new password in an email.
	 * The user must be logged in to use this endpoint.
	 * - TengivaAdmin has authorization to do this
	 * - If the Identity is the same user as the request body's ID field, they can do it.
	 * - If the Identity is NOT the same as the request body's ID, we need to get the organization ids from the DB for the the user ID & check that the Identity is an organizationAdmin for the $user's organization, if yes give authorization
	 * 
	 * @param {sendNewTemporaryPasswordPasswordFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const sendNewTemporaryPasswordPassword = async <T = any>(formData: sendNewTemporaryPasswordPasswordFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/password/sendNewTemporaryPassword`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Creates a random password for a given user, then emails them the password.
	 * 
	 * @param {sendNewTemporaryPasswordAndWelcomeEmailPasswordFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const sendNewTemporaryPasswordAndWelcomeEmailPassword = async <T = any>(formData: sendNewTemporaryPasswordAndWelcomeEmailPasswordFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/password/sendNewTemporaryPasswordAndWelcomeEmail`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the permissions for a given User
	 * 
	 * @param {ReactiveString} user_id - 
	 * @param {ReactiveString} organization_id - Organization get the permissions for
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getByUserIdPermission = async <T = any>(user_id: ReactiveString, organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/permissions/getByUserId`,
			[
				user_id, 
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Check if a User has a Permission
	 * 
	 * @param {hasPermissionPermissionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const hasPermissionPermission = async <T = any>(formData: hasPermissionPermissionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/permissions/hasPermission`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Check if a user has at least one of a set of Permissions
	 * 
	 * Will return true if the user has any one of the Permissions passed.
	 * False if they don't have any of those permissions
	 * 
	 * @param {hasPermissionsPermissionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const hasPermissionsPermission = async <T = any>(formData: hasPermissionsPermissionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/permissions/hasPermissions`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listRoles = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/roles/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} role - Role id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewRole = async <T = any>(role: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/roles/view`,
			[
				role
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addRoleFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addRole = async <T = any>(formData: addRoleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/roles/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} role - Role id.
	 * @param {editRoleFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editRole = async <T = any>(role: ReactiveString, formData?: editRoleFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/roles/edit`,
			[
				role
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * Only sets its status to 0
	 * 
	 * @param {ReactiveString} role - Role id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteRole = async <T = any>(role: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/roles/delete`,
			[
				role
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all the SSO Logins for the current user
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listSsoLogins = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/sso_logins/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the SSO Login for a User & Partner
	 * 
	 * @param {ReactiveString} ssoLogin - Sso Login id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewSsoLogin = async <T = any>(ssoLogin: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/sso_logins/view`,
			[
				ssoLogin
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add a new SSO Login
	 * 
	 * @param {addSsoLoginFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addSsoLogin = async <T = any>(formData: addSsoLoginFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/sso_logins/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete an SSO Login for the current User
	 * 
	 * @param {ReactiveString} ssoLogin - Sso Login id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteSsoLogin = async <T = any>(ssoLogin: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/sso_logins/delete`,
			[
				ssoLogin
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all the SSO Partners
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listSsoPartners = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/sso_partners/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listUserInvitesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listUserInvites = async <T = any>(formData?: listUserInvitesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/user_invites/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} userInvite - User Invite id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewUserInvite = async <T = any>(userInvite: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/user_invites/view`,
			[
				userInvite
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all User Invite matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdUserInvite = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/user_invites/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Accept method
	 * 
	 * @param {ReactiveString} userInvite - User Invite id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const acceptUserInvite = async <T = any>(userInvite: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/user_invites/accept`,
			[
				userInvite
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Reject method
	 * 
	 * @param {ReactiveString} userInvite - User Invite id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const rejectUserInvite = async <T = any>(userInvite: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/user_invites/reject`,
			[
				userInvite
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listUsersActivitiesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listUsersActivities = async <T = any>(formData?: listUsersActivitiesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/users_activities/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} usersActivity - Users Activity id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewUsersActivity = async <T = any>(usersActivity: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users_activities/view`,
			[
				usersActivity
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addUsersActivityFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addUsersActivity = async <T = any>(formData: addUsersActivityFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/users_activities/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Show user currently logged in
	 * 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listUsers = async <T = any>(options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/users/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific user by user id
	 * 
	 * @param {ReactiveString} user - User id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewUser = async <T = any>(user: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/view`,
			[
				user
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View the first & last name for a set of User ids
	 * 
	 * @param {ReactiveString} return_format - Format for the returned results
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 * @ null  (default): Array users data `[{id: 123, first_name: Hugh, last_name: Pearson}]` 
	 * @ "object":  Object the Users' IDs pointing the users' data 
	 * @{ 123:  {id: 123, first_name: Hugh, last_name: Pearson} 
	 */
	const idsToNamesUser = async <T = any>(return_format: ReactiveString, formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/idsToNames`,
			[
				return_format
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit a user whose id matches the id specified in the data
	 * - TengivaAdmin has always authorization.
	 * - If the Identity is the same user as as $user parameter, they can do it.
	 * - If the Identity is NOT the same as the $user parameter,
	 * we need to get the organization id from the DB for the $user parameter
	 * & check that the Identity is an organizationAdmin for the $user's organization
	 * - if yes give read + update authorization
	 * 
	 * @param {editUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editUser = async <T = any>(formData: editUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/users/edit`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Deactivate a User & unset their email address
	 * 
	 * @param {ReactiveString} user - User id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteUser = async <T = any>(user: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/delete`,
			[
				user
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Verifies if the email provided in the JSON exists in the users' table.
	 * Returns a JSON only containing a boolean value.
	 * 
	 * @param {emailExistsUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const emailExistsUser = async <T = any>(formData: emailExistsUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/users/emailExists`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Lists all users. Available only for Tengiva users due to potential security concerns
	 * 
	 * @param {listAllUserFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listAllUser = async <T = any>(formData?: listAllUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/users/listAll`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Invite someone to set up join an existing Organization in Tengiva
	 * 
	 * @param {inviteUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 * @deprecated   Use OrganizationMembersController::invite() 
	 */
	const inviteUser = async <T = any>(formData: inviteUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/users/invite`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Send an activation email for a user.
	 * 
	 * Currently only supports the invite email for new Organizations,
	 * but can be updated later on to support other activation emails (i.e. existing Org, Individuals)
	 * 
	 * @param {ReactiveString} user - User send the invite
	 * @param {ReactiveString} version - Version the activation email send out. One of:
	 * @param {sendActivationEmailUserFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 * @ `newOrganizationInvite`:  For the *Admin* user new *Marketplace* Organization 
	 * @ `newMerchantOrganizationInvite`:  For the *Admin* user new *Merchant* Organization 
	 * @ `newbuyerorganizationinvite`:  For the *Admin* user new *Buyer* *Partner* Organization 
	 */
	const sendActivationEmailUser = async <T = any>(user: ReactiveString, version: ReactiveString, formData?: sendActivationEmailUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/sendActivationEmail`,
			[
				user, 
				version
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Resend an activation email for a User.
	 * 
	 * Supports almost all email versions that are triggered by Users
	 * (Sign ups on Auth or Invites from System)
	 * For Organization Member Invites, this cannot differentiate between
	 * Org. Services used, so every non-Tengiva Org. gets the generic Tengiva email,
	 * not the one specific to their Organization Type.
	 * For Accounts created by Tengiva, use `sendActivationEmail`.
	 * 
	 * @param {ReactiveString} user - User send the invite
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const resendActivationEmailUser = async <T = any>(user: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/users/resendActivationEmail`,
			[
				user
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get an activation link for a User
	 * 
	 * So Tengiva can get a copy of a Users' activation link if the user is
	 * struggling with getting the activation email
	 * 
	 * @param {ReactiveString} user - User get activation link for
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getActivationLinkUser = async <T = any>(user: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/getActivationLink`,
			[
				user
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all users for a specific organization id
	 * 
	 * @param {ReactiveString} organization_id - The the organization
	 * @param {getUsersByOrganizationIdUserFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getUsersByOrganizationIdUser = async <T = any>(organization_id: ReactiveString, formData?: getUsersByOrganizationIdUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/getUsersByOrganizationId`,
			[
				organization_id
			]
		);
		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get the Organization ID for a specific User id
	 * 
	 * @param {ReactiveString} user_id - The the User
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getOrganizationIdUser = async <T = any>(user_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/getOrganizationId`,
			[
				user_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get a User's Roles
	 * 
	 * @param {ReactiveString} user_id - User id.
	 * @param {ReactiveString} organization_id - Organization (Default: all orgs)
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getRolesUser = async <T = any>(user_id: ReactiveString, organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/getRoles`,
			[
				user_id, 
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Give a Role to a User
	 * 
	 * - Organization Members can assign their own role to users within their Organization
	 * - Organization Admins can assign any role to users within their Organization
	 * - Tengiva Members can assign any role to non-Tengiva Organization members
	 * Will update the role if the user already has this role
	 * 
	 * @param {ReactiveString} user - Resolved from the UUID
	 * @param {giveRoleUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const giveRoleUser = async <T = any>(user: ReactiveString, formData: giveRoleUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/giveRole`,
			[
				user
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Revoke a Role from a User
	 * 
	 * @param {ReactiveString} user - Resolved from the UUID
	 * @param {revokeRoleUserFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const revokeRoleUser = async <T = any>(user: ReactiveString, formData: revokeRoleUserFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users/revokeRole`,
			[
				user
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listUsersDataFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listUsersData = async <T = any>(formData?: listUsersDataFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/users_data/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} usersData - Users Data id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewUsersData = async <T = any>(usersData: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users_data/view`,
			[
				usersData
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View user data for the current User by field name & platform
	 * 
	 * @param {ReactiveString} field - Users Data Field
	 * @param {ReactiveString} platform - Platform Name (null for all)
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 * @deprecated   use /viewByFieldAndPlatformAndUserId 
	 */
	const viewByFieldAndPlatformUsersData = async <T = any>(field: ReactiveString, platform: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users_data/viewByFieldAndPlatform`,
			[
				field, 
				platform
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View user data for the current User by field name & platform
	 * If the request is Internal it is possible to fetch a given user
	 * 
	 * @param {viewByFieldAndPlatformAndUserIdUsersDataFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewByFieldAndPlatformAndUserIdUsersData = async <T = any>(formData: viewByFieldAndPlatformAndUserIdUsersDataFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/users_data/viewByFieldAndPlatformAndUserId`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addUsersDataFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addUsersData = async <T = any>(formData: addUsersDataFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/users_data/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} usersData - Users Data id.
	 * @param {editUsersDataFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editUsersData = async <T = any>(usersData: ReactiveString, formData?: editUsersDataFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users_data/edit`,
			[
				usersData
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} usersData - Users Data id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteUsersData = async <T = any>(usersData: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/users_data/delete`,
			[
				usersData
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};


	return {
		call,
		loginAuth,
		createSsoAuthorizationAuth,
		createCheckoutAuthorizationAuth,
		tokenAuth,
		userAuth,
		activateAuth,
		deleteGuest,
		listIndividualMembers,
		viewIndividualMember,
		addIndividualMember,
		editIndividualMember,
		deleteIndividualMember,
		viewByUserIdIndividualMember,
		viewAllByOrganizationIdAndUserIdOrganizationMember,
		addOrganizationMember,
		changeTestAccountOrganizationOrganizationMember,
		inviteOrganizationMember,
		changePassword,
		recoverPassword,
		changeUsingRecoveryCodePassword,
		sendNewTemporaryPasswordPassword,
		sendNewTemporaryPasswordAndWelcomeEmailPassword,
		getByUserIdPermission,
		hasPermissionPermission,
		hasPermissionsPermission,
		listRoles,
		viewRole,
		addRole,
		editRole,
		deleteRole,
		listSsoLogins,
		viewSsoLogin,
		addSsoLogin,
		deleteSsoLogin,
		listSsoPartners,
		listUserInvites,
		viewUserInvite,
		viewAllByOrganizationIdUserInvite,
		acceptUserInvite,
		rejectUserInvite,
		listUsersActivities,
		viewUsersActivity,
		addUsersActivity,
		listUsers,
		viewUser,
		idsToNamesUser,
		editUser,
		deleteUser,
		emailExistsUser,
		listAllUser,
		inviteUser,
		sendActivationEmailUser,
		resendActivationEmailUser,
		getActivationLinkUser,
		getUsersByOrganizationIdUser,
		getOrganizationIdUser,
		getRolesUser,
		giveRoleUser,
		revokeRoleUser,
		listUsersData,
		viewUsersData,
		viewByFieldAndPlatformUsersData,
		viewByFieldAndPlatformAndUserIdUsersData,
		addUsersData,
		editUsersData,
		deleteUsersData,
	}
};